/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { debounce } from "lodash";
import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import CreateRecordButton from "../input/CreateRecordButton";
import notificationState from "../../states/notificationState";
import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
/**
 * @COMPONENT_NAME CommonCodeMst
 * @DESCRIPTION 공통코드 마스터 관리를 위한 임베디드 컴포넌트입니다.
 *
 * @PROPS
 *    - {Function} onSelect - 마스터 그리드 선택 시 호출되는 콜백 함수
 *	  - {Function} setCall - 마스터 그리드 데이터 수정 시 호출되는 setState 함수
 * @CALLBACKS
 *    - {Function} onSelect - 마스터 그리드가 선택되었을 때 부모 컴포넌트에 알리는 콜백
 *	  - {Function} onApiInitialized - API 초기화 시 호출되는 콜백 함수
 *	  - {Function} onFailure - 등록, 수정, 삭제 실패 시 호출되는 콜백 함수
 *	  - {Function} onNotifyWarning - 등록, 수정, 삭제 시 경고 트리거 시 경고 알림 콜백 함수
 *	  - {Function} onSuccess - 등록, 수정, 삭제 시 호출되는 콜백 함수
 */
function CommonCodeMst(props) {
	const { t } = useTranslation();
	const { onSelect, setCall } = props;

	const table = "CommonCodeMst";
	const view = "VwCommonCodeMst";
	const setNotification = useSetRecoilState(notificationState);
	const [schema, setSchema] = useState();
	const [api, setApi] = useState();
	const [grid, setGrid] = useState();
	const [selectedMst, setSelectedMst] = useState();
	useEffect(() => {
		let mounted = true;

		async function schemaSetting() {
			let schema = await getSchema(table);

			if (mounted) {
				setSchema(schema);
			}
		}

		schemaSetting();
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (grid) {
			grid.selectionChanged.addHandler(
				debounce((grid, event) => {
					if (grid.selectedItems && grid.selectedItems[0]) {
						if (onSelect) {
							onSelect(grid.selectedItems[0]);
							setSelectedMst(grid.selectedItems[0]);
						}
					} else {
						if (onSelect) {
							onSelect();
							setSelectedMst();
						}
					}
				}, 10)
			);

			grid.itemsSourceChanged.addHandler(
				debounce((grid, event) => {
					if (grid.selectedItems && grid.selectedItems[0]) {
						if (onSelect) {
							onSelect(grid.selectedItems[0]);
							setSelectedMst(grid.selectedItems[0]);
						}
					} else {
						if (onSelect) {
							onSelect();
							setSelectedMst();
						}
					}
				}, 10)
			);
		}
	}, [grid, onSelect]);
	const onApiInitialized = (api) => {
		setApi(api);
	};
	const onGridInitialized = (grid) => {
		grid.selectionMode = "Row";
		setGrid(grid);
	};

	const notifySuccess = (msg) => {
		setNotification({
			open: true,
			severity: "success",
			msg: msg,
		});
	};
	const onSuccess = async (msg) => {
		notifySuccess(msg);

		// 1. 현재 선택된 row의 PK 또는 고유 값 기억
		const currentSelectedKey = selectedMst?.mstCodeId; // 또는 selectedMst?.[primaryKeyName]
		// 2. refresh 후 해당 key를 가진 row를 다시 선택
		if (api && currentSelectedKey != null) {
			await api.refresh();

			if (grid && grid.collectionView) {
				const matchedItem = grid.collectionView.items.find(
					(item) => item.mstCodeId === currentSelectedKey
				);
				if (matchedItem) {
					grid.select(
						grid.collectionView.items.indexOf(matchedItem),
						0
					);
				}
			}

			// 3. 하위 화면 갱신을 위한 setCall
			if (setCall) setCall(Date.now());
		}
	};

	const notifyFailure = (msg) => {
		setNotification({
			open: true,
			severity: "error",
			msg: msg,
		});
	};

	const onFailure = (reason) => {
		notifyFailure(reason.response ? reason.response.data : reason);
	};

	const notifyWarning = (msg) => {
		setNotification({
			open: true,
			severity: "warning",
			msg: msg,
		});
	};

	const onNotifyWarning = (msg) => {
		notifyWarning(msg);
		if (api) {
			api.refresh();
		}
	};
	return (
		<BasicCrudScreenType
			id={table}
			headerTitle={t("term.commonCodeMst")}
			table={table}
			view={view}
			noCreate
			noEdit
			noDelete
			noExcel
			onInitialized={onGridInitialized}
			onApiInitialized={onApiInitialized}
			onAppendButtonInFrontOfSearch={() => (
				<>
					<BasicCrudScreenActionItemLayout>
						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod="POST"
						>
							<CreateRecordButton
								id={table}
								schema={schema}
								refresh={api}
								onSuccess={onSuccess}
							/>
						</AccessControl>

						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod={
								schema &&
								(schema.updateWithPatch ? "PATCH" : "PUT")
							}
						>
							<UpdateRecordButton
								id={table}
								schema={schema}
								grid={grid}
								refresh={api}
								onSuccess={onSuccess}
								disabled={!selectedMst}
							/>
						</AccessControl>

						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod="DELETE"
						>
							<DeleteRecordButton
								id={table}
								schema={schema}
								grid={grid}
								refresh={api}
								disabled={!selectedMst}
								color="secondary"
								onSuccess={onSuccess}
								onFailure={onFailure}
								onPartialFailure={onNotifyWarning}
							/>
						</AccessControl>
					</BasicCrudScreenActionItemLayout>
				</>
			)}
		/>
	);
}

export default CommonCodeMst;
